import _ from 'lodash';
import CONTENT_STRINGS from './contentStrings';
import { CURRENCY } from './variables';
import {
    PREMIUM,
    RECOVERABLE,
    NON_RECOVERABLE,
    UNDERWRITER_RATE,
    COMMISSION,
    COMMISSION_PERCENTAGE,
    UNDERWRITER_RATE_PERCENTAGE,
    CLINICAL_NEGLIGENCE_MN,
    CONTINGENT,
    NON_CONTINGENT,
    PS_TYPE_PERCENTAGE,
    PS_TYPE_DEFAULT,
    UNDERWRITER_RATE_SOURCE,
    RECOVERABLE_SOURCE,
    COMMISSION_SOURCE,
    SOURCE_AMOUNT,
    SOURCE_PERCENTAGE,
} from './constants';
import { validateAmount, formattedMoney, roundToFourDecimals } from './functions';

// The real, full-precision computed amount (e.g. underwriterRate/commission derived from a
// typed percentage) — this is what gets stored/submitted. The UI only rounds to 2 decimals
// at render time for display; it never truncates the value kept in state.
const calcAmountFromPercentage = (percentage, premium) =>
    roundToFourDecimals((percentage / 100) * premium).toString();

// Percentage fields auto-calculated from amounts legitimately carry up to 4 decimals.
const calcPercentageFromAmount = (amount, premium) =>
    roundToFourDecimals((amount / premium) * 100).toString();

// The real, full-precision remainder (e.g. non-recoverable = premium - recoverable) — same
// rule as calcAmountFromPercentage: store the real value, round only for display.
const calcRemainingAmount = (total, part) =>
    roundToFourDecimals(total - part).toString();

const standardViewOfChargeTypes = (chargeTypes, hasCommissions, isClinicalNegligence) => {
    chargeTypes.push({ key: PREMIUM, label: CONTENT_STRINGS.PREMIUM });
    if (isClinicalNegligence) {
        chargeTypes.push(
            { key: RECOVERABLE, label: CONTENT_STRINGS.RECOVERABLE },
            { key: NON_RECOVERABLE, label: CONTENT_STRINGS.NON_RECOVERABLE }
        );
    }
    chargeTypes.push({ key: UNDERWRITER_RATE, label: CONTENT_STRINGS.UNDERWRITER_RATE });
    if (hasCommissions) {
        chargeTypes.push({ key: COMMISSION, label: CONTENT_STRINGS.COMMISSION });
    }
};

const percentageViewOfChargeTypes = (chargeTypes, hasCommissions, isClinicalNegligence) => {
    if (isClinicalNegligence) {
        chargeTypes.push(
            { key: NON_RECOVERABLE, label: CONTENT_STRINGS.NON_RECOVERABLE },
        );
    }
    chargeTypes.push({ key: UNDERWRITER_RATE, label: CONTENT_STRINGS.UNDERWRITER_RATE });
    if (hasCommissions) {
        chargeTypes.push({ key: COMMISSION, label: CONTENT_STRINGS.COMMISSION });
    }
};

const checkClinicalNegligence = (selectedCaseType) => {
    return selectedCaseType?.machineName === CLINICAL_NEGLIGENCE_MN;
}

// Function to handle raw data for Application Premium Settings and Premium Settings Tables
export const handlePremiumSettingsTableData = (tableData, selectedCaseType, isPercentagePremium = false, showTable, isApplicationPremium) => {
    // First, group tableData by stageId + isNonContingent to keep contingent/NC rows separate
    // (the same stageId can appear in both contingent and non-contingent lists)
    let stages = {};
    tableData.forEach(item => {
        const stageKey = `${item.stageId}-${item.isNonContingent ? NON_CONTINGENT : CONTINGENT}`;
        if (!stages[stageKey]) {
            stages[stageKey] = {
                name: item.stageName,
                items: [],
                id: item.stageId,
                stageSortOrder : item.stageSortOrder,
                premiumType: item.premiumType,
            };
        }
        stages[stageKey].items.push(item);
    });
    // Create rows for each charge type per stage
    let finalData = [];
    let chargeTypes = [];
    const isClinicalNegligence = checkClinicalNegligence(selectedCaseType);
    const hasCommissions = tableData.find(item => item.premiumSubSettingCommissions && item.premiumSubSettingCommissions.length > 0);
    // Determine charge types based on view mode
    if (isPercentagePremium) {
        // Percentage premiums have two different view modes
        // Application percentage premiums view, when showTable is true have the standardViewOfChargeType.
        // During the wizard run OR during showing and editing on the Premium Settings page, 
        // If clinical negligence is selected then only non-recoverable, underwriter rate percentages and commission percentages are shown.
        if(showTable){
            if(isApplicationPremium){
                standardViewOfChargeTypes(chargeTypes, hasCommissions, isClinicalNegligence);
            } else {
                percentageViewOfChargeTypes(chargeTypes, hasCommissions, isClinicalNegligence);
            }
        } else {
            percentageViewOfChargeTypes(chargeTypes, hasCommissions, isClinicalNegligence);
        }
    } else {
        // Standard view shows all charge types
        standardViewOfChargeTypes(chargeTypes, hasCommissions, isClinicalNegligence);
    }

    // Process each stage separately to maintain grouping
    // Sort to match calculateVerticalHeaders order: contingent stages by id, then non-contingent stages by id
    const sortedStages = Object.values(stages).sort((a, b) => {
        const aIsNonCont = !!a.items[0]?.isNonContingent;
        const bIsNonCont = !!b.items[0]?.isNonContingent;
        if (aIsNonCont !== bIsNonCont) return aIsNonCont ? 1 : -1;
        return a.stageSortOrder - b.stageSortOrder || a.id - b.id;
    });
    sortedStages.forEach(stage => {
        const isNonContingent = stage.items[0]?.premiumType === NON_CONTINGENT;
        const outerHeaderId = isNonContingent ? NON_CONTINGENT : CONTINGENT;
        chargeTypes.forEach(chargeType => {
            if ([PREMIUM, RECOVERABLE, NON_RECOVERABLE].includes(chargeType.key)) {
                const row = {
                    stageId: stage.id,
                    chargeType: chargeType.label,
                    name: chargeType.key,
                    stageName: stage.name,
                    stageSortOrder: stage.stageSortOrder,
                    premiumType: stage.premiumType,
                    isNonContingent,
                    outerHeaderId,
                };
                // Collect data array for this row
                const dataArr = [];
                stage.items.forEach((item) => {
                    // Whichever side of the recoverable/non-recoverable pair the user did NOT
                    // just type is the derived remainder — round it for display (see
                    // calcRemainingAmount). Premium is always typed directly, never derived.
                    const amountIsDerived = chargeType.key === RECOVERABLE
                        ? (item[RECOVERABLE_SOURCE] ? item[RECOVERABLE_SOURCE] === NON_RECOVERABLE : true)
                        : chargeType.key === NON_RECOVERABLE
                            ? (item[RECOVERABLE_SOURCE] ? item[RECOVERABLE_SOURCE] === RECOVERABLE : true)
                            : false;
                    const dataObj = {
                        amount: (item[chargeType.key] != null && item[chargeType.key] !== '' ? Number(item[chargeType.key]) : ''),
                        // Untouched string as stored in state — Number(...) above silently drops
                        // a trailing "." or trailing zeros (e.g. "10." -> 10), which would make it
                        // impossible to type "10.5" since every render would snap "10." back to
                        // "10" before the next digit lands. Used only for the editable input's
                        // displayed value; every other consumer keeps using the numeric `amount`.
                        rawAmount: item[chargeType.key],
                        percentage: null,
                        amountIsDerived,
                    };
                    dataArr.push(dataObj);
                });
                row.data = dataArr;
                finalData.push(row);
            } else if (chargeType.key === UNDERWRITER_RATE) {
                const row = {
                    stageId: stage.id,
                    chargeType: chargeType.label,
                    name: [UNDERWRITER_RATE, `${UNDERWRITER_RATE_PERCENTAGE}`],
                    stageName: stage.name,
                    stageSortOrder: stage.stageSortOrder,
                    premiumType: stage.premiumType,
                    isNonContingent,
                    outerHeaderId,
                };
                const dataArr = [];
                stage.items.forEach((item) => {
                    // Amount is derived (round for display) unless the user just typed it
                    // directly — defaults to derived/rounded until the user edits it.
                    const amountIsDerived = item[UNDERWRITER_RATE_SOURCE]
                        ? item[UNDERWRITER_RATE_SOURCE] === SOURCE_PERCENTAGE
                        : true;
                    const dataObj = {
                        amount: (item.underwriterRate != null && item.underwriterRate !== '' ? Number(item.underwriterRate) : ''),
                        // See the comment on the PREMIUM/RECOVERABLE/NON_RECOVERABLE branch above —
                        // preserves what's actually in state (e.g. "10.") for the editable input's
                        // displayed value, since Number(...) above would silently drop the trailing
                        // "." or trailing zeros on every render.
                        rawAmount: item.underwriterRate,
                        percentage: (item.underwriterRatePercentage != null && item.underwriterRatePercentage !== '' ? Number(item.underwriterRatePercentage) : ''),
                        rawPercentage: item.underwriterRatePercentage,
                        amountIsDerived,
                    };
                    dataArr.push(dataObj);
                });
                row.data = dataArr;
                finalData.push(row);
            } else if (chargeType.key === COMMISSION) {
                const commissionRows = new Map();
                stage.items.forEach((item) => {
                    item.premiumSubSettingCommissions?.forEach(commission => {
                        const key = `${commission.lawsysName}-${commission.lawsysId}`;
                        if (!commissionRows.has(key)) {
                            commissionRows.set(key, {
                                ...commission,
                                data: []
                            });
                        }
                        const amountIsDerived = commission[COMMISSION_SOURCE]
                            ? commission[COMMISSION_SOURCE] === SOURCE_PERCENTAGE
                            : true;
                        const dataObj = {
                            amount: (commission.commission != null && commission.commission !== '' ? Number(commission.commission) : ''),
                            // Same as the underwriterRate row above — preserve the raw stored
                            // string for editable display so a trailing "." typed by the user
                            // survives the next render instead of being stripped by Number(...).
                            rawAmount: commission.commission,
                            percentage: (commission.commissionPercentage != null && commission.commissionPercentage !== '' ? Number(commission.commissionPercentage) : ''),
                            rawPercentage: commission.commissionPercentage,
                            amountIsDerived,
                        };
                        commissionRows.get(key).data.push(dataObj);
                    });
                });

                commissionRows.forEach((commission, key) => {
                    if (commission.data.length === 0) return;

                    const lawsysName = `${commission.lawsysName} ${CONTENT_STRINGS.COMMISSION}`;
                    const idMatch = lawsysName.match(/(.*?)\s*(\(ID:.*?\))/);
                    let commissionName = lawsysName;
                    let commissionId = '';
                    if (idMatch) {
                        commissionName = idMatch[1];
                        commissionId = idMatch[2];
                    }

                    finalData.push({
                        stageId: stage.id,
                        solicitorId: commission.lawsysId,
                        chargeType: commissionName,
                        commissionId,
                        aragProvider: commission.aragProvider,
                        name: [COMMISSION, COMMISSION_PERCENTAGE],
                        stageName: stage.name,
                        stageSortOrder: stage.stageSortOrder,
                        premiumType: stage.premiumType,
                        isNonContingent,
                        outerHeaderId,
                        data: commission.data
                    });
                });
            }
        });
    });
    return finalData;
};

// Function to validate the Premium Settings Table. Both Application Premium Settings and Premium Settings Tables
export const validatePremiumTable = (tableData, changeStoreSettings, selectedCaseType, setEmptyField, isPercentagePremium=false) => {
    const isClinicalNegligence = checkClinicalNegligence(selectedCaseType);
    // Check if any required field is empty
    for (const item of tableData) {
        if (isPercentagePremium) {
            if (
                item.nonRecoverable === '' ||
                item.underwriterRatePercentage === ''
            ) {
                setEmptyField(CONTENT_STRINGS.EMPTY_FIELDS);
                return false;
            }
            if(item.premiumSubSettingCommissions && item.premiumSubSettingCommissions.length > 0) {
                for (const commission of item.premiumSubSettingCommissions) {
                    if (commission.commissionPercentage === '') {
                        setEmptyField(CONTENT_STRINGS.EMPTY_FIELDS);
                        return false;
                    }
                }
            }
        } else {
            if (isClinicalNegligence) {
                if (
                    item.premium === '' ||
                    item.recoverable === '' ||
                    item.nonRecoverable === '' ||
                    item.underwriterRate === '' ||
                    item.underwriterRatePercentage === ''
                ) {
                    setEmptyField(CONTENT_STRINGS.EMPTY_FIELDS);
                    return false;
                }
            } else {
                if (
                    item.premium === '' ||
                    item.underwriterRate === '' ||
                    item.underwriterRatePercentage === ''
                ) {
                    setEmptyField(CONTENT_STRINGS.EMPTY_FIELDS);
                    return false;
                }
            }
            for (const commission of item.premiumSubSettingCommissions) {
                if (commission.commission === '' || commission.commissionPercentage === '') {
                    setEmptyField(CONTENT_STRINGS.EMPTY_FIELDS);
                    return false;
                }
            }
        }
    }

    // Decimal-place sanity check. `premium` and percentage-premium's `nonRecoverable` are
    // always directly typed (keystroke-capped at 2dp already), so validate them at 2dp.
    // `recoverable` can be the auto-calculated remainder of the other (calcRemainingAmount),
    // which legitimately carries up to 4dp — the real value, not the 2dp display rounding —
    // so it's validated separately at 4dp to avoid rejecting a valid derived amount.
    for (const item of tableData) {
        const typedAmountFields = isPercentagePremium
            ? [item.nonRecoverable]
            : [item.premium];
        const derivedAmountFields = (!isPercentagePremium && isClinicalNegligence)
            ? [item.recoverable]
            : [];

        for (const value of typedAmountFields) {
            if (value !== '' && value !== null && value !== undefined) {
                const errMsg = validateAmount(value, 0, 2);
                if (errMsg) { setEmptyField(errMsg); return false; }
            }
        }
        for (const value of derivedAmountFields) {
            if (value !== '' && value !== null && value !== undefined) {
                const errMsg = validateAmount(value, 0, 4);
                if (errMsg) { setEmptyField(errMsg); return false; }
            }
        }
    }

    // Check if sum of underwriterRatePercentage and commissions exceeds 100%
    for (const item of tableData) {
        const commissionsSum = item.premiumSubSettingCommissions.reduce(
            (sum, commission) => sum + parseFloat(commission.commissionPercentage || 0),
            0
        );
        const totalPercentage = parseFloat(item.underwriterRatePercentage || 0) + commissionsSum;
        if (totalPercentage > 100) {
            changeStoreSettings('validationMessage', CONTENT_STRINGS.SUM_OF_UNDERWRITER_RATE_AND_SOLICITOR_COMMISSION_RATE);
            return false;
        }
    }

    // Check for negative values
    for (const item of tableData) {
        if (
            parseFloat(item.premium) < 0 ||
            parseFloat(item.recoverable) < 0 ||
            parseFloat(item.nonRecoverable) < 0 ||
            parseFloat(item.underwriterRate) < 0 ||
            parseFloat(item.underwriterRatePercentage) < 0
        ) {
            changeStoreSettings('validationMessage', CONTENT_STRINGS.NEGATIVE_FIELDS);
            return false;
        }

        for (const commission of item.premiumSubSettingCommissions) {
            if (
                parseFloat(commission.commission) < 0 || 
                parseFloat(commission.commissionPercentage) < 0
            ) {
                changeStoreSettings('validationMessage', CONTENT_STRINGS.NEGATIVE_FIELDS);
                return false;
            }
        }
    }

    changeStoreSettings('validationMessage', '');
    return true;
};
//function that return the step message
export const getStepMessage = (stepNumber, stepMessages) => {
    if(stepMessages.length === 0)
        return '';
    let stepMessage = '';
    _.forEach(stepMessages, message => {
        if(message.position.includes(stepNumber.toString())){
            stepMessage = message.body;
            return false;
        }
    });
    return stepMessage;
};

export const calculateVerticalHeaders = (contingentCaseStages, nonContingentCaseStages) => {
    const hasContingent = contingentCaseStages && contingentCaseStages.length > 0;
    const hasNonContingent = nonContingentCaseStages && nonContingentCaseStages.length > 0;
    contingentCaseStages = _.orderBy(contingentCaseStages, ['sortOrder', 'id']);
    nonContingentCaseStages = _.orderBy(nonContingentCaseStages, ['sortOrder', 'id']);
    if (hasContingent && hasNonContingent) {
        const contingentHeaders = contingentCaseStages.map(stage => ({ header: stage.name, machineName: stage.machineName, outerHeaderId: CONTINGENT }));
        const nonContingentHeaders = nonContingentCaseStages.map(stage => ({ header: stage.name, machineName: stage.machineName, outerHeaderId: NON_CONTINGENT }));
        const combinedHeaders = [...contingentHeaders, ...nonContingentHeaders];
        return combinedHeaders.map((item, key) => ({
            ...item,
            id: key.toString() // Ensure unique IDs for all headers
        }));
    }
    if (hasNonContingent) {
        return nonContingentCaseStages.map((stage, key) => ({ header: stage.name, machineName: stage.machineName, id: key.toString(), outerHeaderId: NON_CONTINGENT }));
    }
    if (hasContingent) {
        return contingentCaseStages.map((stage, key) => ({ header: stage.name, machineName: stage.machineName, id: key.toString(), outerHeaderId: CONTINGENT }));
    }
    return [];
};

export const calculateOuterVerticalHeaders = (contingentCaseStages, nonContingentCaseStages) => {
    const hasContingent = contingentCaseStages && contingentCaseStages.length > 0;
    const hasNonContingent = nonContingentCaseStages && nonContingentCaseStages.length > 0;

    if (hasContingent && hasNonContingent) {
        return [
            { header: CONTENT_STRINGS.CONTINGENT, outerHeaderId: CONTINGENT },
            { header: CONTENT_STRINGS.NON_CONTINGENT, outerHeaderId: NON_CONTINGENT }
        ];
    }
    if (hasNonContingent) {
        return [ { header: CONTENT_STRINGS.NON_CONTINGENT, outerHeaderId: NON_CONTINGENT } ];
    }
    if (hasContingent) {
        return [ { header: CONTENT_STRINGS.CONTINGENT, outerHeaderId: CONTINGENT } ];
    }
    return null;
};

export const calculateGroupColumns = (columns) => {
    const groups = columns.map(column => {
        if (column.propertyName !== 'chargeType') {
            return column.id;
        }
        return null; // Explicitly return null for chargeType (though undefined would work too)
    }).filter(Boolean); // This removes all falsy values (null, undefined, etc.)
    return groups;
}

export const safeParseFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
};

	//function that finds the solicitor index in the table
export const findSolicitorIndex = (tableIndex, objectRow, tableData) => {
	return _.findIndex(tableData[tableIndex].premiumSubSettingCommissions, (item) => {
		return item.lawsysId === objectRow.solicitorId;
	});
};

export const getTableIndex = (caseStageId, columnIndex, tableData, isNonContingent = false) => {
    //get a table index by caseStageId, damageBandIndex and isNonContingent
    let index = -1;
    _.forEach(tableData, (item, index2) => {
        if (item.stageId === caseStageId && item.damageBandIndex === columnIndex && !!item.isNonContingent === isNonContingent) {
            index = index2;
            return false;
        }
    });
    return index;
};

//function that changes the store for percentage premium settings
export const handlePercentagePremiumTableChange = (field, value, objectRow, columnIndex, premiumSettingsWizard, changeStore2, changeStore3) => {
    const tableData = premiumSettingsWizard.tableData;
    const tableIndex = getTableIndex(objectRow.stageId, columnIndex, tableData, objectRow.isNonContingent);
    const solicitorIndex = findSolicitorIndex(tableIndex, objectRow, tableData);

    if (field === NON_RECOVERABLE) {
        changeStore2(NON_RECOVERABLE, value, tableIndex);
        // Mirrors the flat handler (handleRecoverableAndNonRecoverableChange) — without this,
        // recoverableSource is never set, amountIsDerived stays permanently true, and the
        // editable input re-renders with the rounded numeric amount on every keystroke,
        // stripping a trailing "." before the user can type a decimal digit.
        changeStore2(RECOVERABLE_SOURCE, NON_RECOVERABLE, tableIndex);
    } else if (field === UNDERWRITER_RATE_PERCENTAGE) {
        changeStore2(UNDERWRITER_RATE_PERCENTAGE, value, tableIndex);
    } else if (field === COMMISSION_PERCENTAGE) {
        changeStore3(COMMISSION_PERCENTAGE, value, tableIndex, solicitorIndex);
    }
};

//function that changes the store underwriter and premium
export const handlePremiumAndUnderwriterChange = (newValue, field, objectRow, columnIndex, premiumSettingsWizard, changeStore2, changeStore3, lockContingentCommission = false, isGplSolicitorGroup = false) => {
    const tableData = premiumSettingsWizard.tableData;
    const tableIndex = getTableIndex(objectRow.stageId, columnIndex, tableData, objectRow.isNonContingent);
    if (tableIndex === -1) return;
    if(field === PREMIUM) {
        // WI 97790: on a restricted policy the contingent underwriter and commission cells are read-only,
        // so blanking them here would leave values the user has no way to refill and a form that cannot be
        // saved. Hold each stored percentage and re-derive its amount from the new premium instead.
        // Recoverable/Non-Recoverable are still cleared - they stay editable, so the user retypes them
        // exactly as before.
        const freezeSplit = lockContingentCommission && !objectRow.isNonContingent;
        const row = tableData[tableIndex];
        const premiumFloat = parseFloat(newValue);
        const premiumIsUsable = newValue !== '' && !isNaN(premiumFloat);

        changeStore2(field, newValue, tableIndex);
        // GPL AC1/AC5 - the actual Underwriter Rate/Amount reset for this edit happens below, in
        // whichever of the freezeSplit/else branches actually runs (both unconditionally rewrite these
        // two fields) - isGplSolicitorGroup is threaded into that final reset, not here, so a change to
        // either branch's logic can't silently stop respecting it the way a duplicate reset up here once did.
        changeStore2(RECOVERABLE, '', tableIndex);
        changeStore2(NON_RECOVERABLE, '', tableIndex);
        changeStore2(RECOVERABLE_SOURCE, '', tableIndex);

        if (freezeSplit) {
            // GPL AC1/AC5 - a row can be both WI 97790-restricted and Green/LEI at once (Application
            // Premium Settings on an Abandoned/Lost policy with no PIG, for a GPL group). Force 0 rather
            // than deriving from the stored percentage: deriving would (a) resurrect a legacy non-zero
            // rate into wizard state for a cell that renders locked at 0, and (b) if the premium is
            // blanked, write '' into a disabled field validatePremiumTable rejects and the user cannot
            // fix, since there's no way to type into a disabled input.
            const underwriterPercentage = isGplSolicitorGroup ? 0 : row.underwriterRatePercentage;
            const hasUnderwriterPercentage = underwriterPercentage !== '' && underwriterPercentage != null;
            const underwriterRate = isGplSolicitorGroup
                ? 0
                : (premiumIsUsable && hasUnderwriterPercentage)
                    ? calcAmountFromPercentage(parseFloat(underwriterPercentage), premiumFloat)
                    : '';
            changeStore2(UNDERWRITER_RATE_PERCENTAGE, underwriterPercentage, tableIndex);
            changeStore2(UNDERWRITER_RATE, underwriterRate, tableIndex);
            // The amount is derived from the percentage, not typed - the cell formatter reads this marker
            // to decide whether to round for display (see handlePremiumSettingsTableData).
            changeStore2(UNDERWRITER_RATE_SOURCE, SOURCE_PERCENTAGE, tableIndex);

            _.forEach(row.premiumSubSettingCommissions, (item, index) => {
                const commissionPercentage = item.commissionPercentage;
                const hasCommissionPercentage = commissionPercentage !== '' && commissionPercentage != null;
                changeStore3(COMMISSION_PERCENTAGE, commissionPercentage, tableIndex, index);
                changeStore3(
                    COMMISSION,
                    (premiumIsUsable && hasCommissionPercentage)
                        ? calcAmountFromPercentage(parseFloat(commissionPercentage), premiumFloat)
                        : '',
                    tableIndex,
                    index
                );
                changeStore3(COMMISSION_SOURCE, SOURCE_PERCENTAGE, tableIndex, index);
            });
        } else {
            // GPL AC1/AC5 - see the isGplSolicitorGroup comment above: this branch runs for every
            // Premium edit outside a locked/restricted contingent row (i.e. always, for the standalone
            // wizard, since lockContingentCommission is only ever true on Application Premium Settings),
            // so it must not undo the zero-seed here either.
            changeStore2(UNDERWRITER_RATE, isGplSolicitorGroup ? 0 : '', tableIndex);
            changeStore2(UNDERWRITER_RATE_PERCENTAGE, isGplSolicitorGroup ? 0 : '', tableIndex);
            changeStore2(UNDERWRITER_RATE_SOURCE, '', tableIndex);
            _.forEach(row.premiumSubSettingCommissions, (item, index) => {
                changeStore3(COMMISSION, '', tableIndex, index);
                changeStore3(COMMISSION_PERCENTAGE, '', tableIndex, index);
                changeStore3(COMMISSION_SOURCE, '', tableIndex, index);
            })
        }
    }
    else if(field === UNDERWRITER_RATE){
        if(newValue === ''){
            changeStore2(UNDERWRITER_RATE, '', tableIndex);
            changeStore2(UNDERWRITER_RATE_PERCENTAGE, '', tableIndex);
        }
        else {
            const floatParsed = parseFloat(newValue);
            const premiumFloat = parseFloat(tableData[tableIndex].premium);
            const finalPercentage = calcPercentageFromAmount(floatParsed, premiumFloat);

            changeStore2(field, newValue, tableIndex);
            changeStore2(UNDERWRITER_RATE_PERCENTAGE, finalPercentage , tableIndex);
            // The user is typing the amount directly — show it raw (not rounded-for-display)
            // while its sibling percentage is now the derived value.
            changeStore2(UNDERWRITER_RATE_SOURCE, SOURCE_AMOUNT, tableIndex);
        }
    }
    else if(field === UNDERWRITER_RATE_PERCENTAGE){

        if(newValue === ''){
            changeStore2(field, newValue, tableIndex);
            changeStore2(UNDERWRITER_RATE, '', tableIndex);
        }
        else {
            const floatParsed = parseFloat(newValue);
            const premiumFloat = parseFloat(tableData[tableIndex].premium);
            const finalValue = calcAmountFromPercentage(floatParsed, premiumFloat);
            changeStore2(field, newValue, tableIndex);
            changeStore2(UNDERWRITER_RATE, finalValue, tableIndex);
            // The amount is now the derived value (computed from the typed percentage) —
            // the table cell formatter rounds it to 2dp for display, without touching this
            // real 4dp value kept here for submission.
            changeStore2(UNDERWRITER_RATE_SOURCE, SOURCE_PERCENTAGE, tableIndex);
        }
    }
};

//function that changes the store commissions
export const handleCommissionChange = (newValue, field, objectRow, columnIndex, premiumSettingsWizard, changeStore3) => {
    const tableData = premiumSettingsWizard.tableData;
    const tableIndex = getTableIndex(objectRow.stageId, columnIndex, tableData, objectRow.isNonContingent);
    if (tableIndex === -1) return;
    if(field === COMMISSION){
        const solicitorIndex = findSolicitorIndex(tableIndex, objectRow, tableData);
        if(newValue === ''){
            changeStore3(field, newValue, tableIndex, solicitorIndex);
            changeStore3(COMMISSION_PERCENTAGE, '', tableIndex, solicitorIndex);
        }
        else {
            const floatParsed = parseFloat(newValue);
            const premiumFloat = parseFloat(tableData[tableIndex].premium);
            const finalPercentage = calcPercentageFromAmount(floatParsed, premiumFloat);
            changeStore3(field, newValue, tableIndex, solicitorIndex);
            changeStore3(COMMISSION_PERCENTAGE, finalPercentage, tableIndex, solicitorIndex);
            // Amount typed directly — show raw, not rounded-for-display.
            changeStore3(COMMISSION_SOURCE, SOURCE_AMOUNT, tableIndex, solicitorIndex);
        }
    }
    else if(field === COMMISSION_PERCENTAGE){
        const solicitorIndex = findSolicitorIndex(tableIndex, objectRow, tableData);
        if(newValue === ''){
            changeStore3(field, newValue, tableIndex, solicitorIndex);
            changeStore3(COMMISSION, '', tableIndex, solicitorIndex);
        }
        else {
            const floatParsed = parseFloat(newValue);
            const premiumFloat = parseFloat(tableData[tableIndex].premium);
            const finalValue = calcAmountFromPercentage(floatParsed, premiumFloat);
            changeStore3(field, newValue, tableIndex, solicitorIndex);
            changeStore3(COMMISSION, finalValue, tableIndex, solicitorIndex);
            // Amount is now derived from the typed percentage — round it for display only.
            changeStore3(COMMISSION_SOURCE, SOURCE_PERCENTAGE, tableIndex, solicitorIndex);
        }
    }
};

//function that changes the store recoverable and non recoverable
export const sortDamageBands = (damageBands) => {
	return _.sortBy(damageBands, (band) => band[0].lowValue);
};

export const areDamagesNeighbors = (damagesSortOrders, damageSortOrder1, damageSortOrder2) => {
	const index = _.findIndex(damagesSortOrders, item => item === damageSortOrder1);
	return damagesSortOrders[index + 1] === damageSortOrder2;
};

export const getDamageBandName = (damageBand, damages) => {
	if (damageBand.length === damages.length) {
		return CONTENT_STRINGS.UNLIMITED;
	}
	if (!_.last(damageBand)) {
		return '';
	}
	if (_.last(damageBand).highValue === 0) {
		return `${CONTENT_STRINGS.OVER} ` + formattedMoney(damageBand[0].lowValue - 1, CURRENCY, 0);
	}
	return formattedMoney(damageBand[0].lowValue, CURRENCY, 0) + ' - ' + formattedMoney(_.last(damageBand).highValue, CURRENCY, 0);
};

export const handleRecoverableAndNonRecoverableChange = (newValue, field, objectRow, columnIndex, premiumSettingsWizard, changeStore2) => {
    const tableData = premiumSettingsWizard.tableData;
    const tableIndex = getTableIndex(objectRow.stageId, columnIndex, tableData, objectRow.isNonContingent);
    if (tableIndex === -1) return;
    if(field === RECOVERABLE) {//when user specifies premium underwriter rate , its percentage and the solicitor commissions must be filled from the start
        if(newValue === ''){
            changeStore2(NON_RECOVERABLE, '', tableIndex);
            changeStore2(RECOVERABLE, '', tableIndex);
        }
        else {
            const premiumFloat = parseFloat(tableData[tableIndex].premium);
            const newValueFloat = parseFloat(newValue);
            changeStore2(field, newValue, tableIndex);
            changeStore2(NON_RECOVERABLE, calcRemainingAmount(premiumFloat, newValueFloat), tableIndex);
            // Recoverable typed directly — non-recoverable is now the derived remainder.
            changeStore2(RECOVERABLE_SOURCE, RECOVERABLE, tableIndex);
        }
    }
    else if(field === NON_RECOVERABLE){
        if(newValue === ''){
            changeStore2(NON_RECOVERABLE, '', tableIndex);
            changeStore2(RECOVERABLE, '', tableIndex);
        }
        else {
            const premiumFloat = parseFloat(premiumSettingsWizard.tableData[tableIndex].premium);
            const newValueFloat = parseFloat(newValue);
            changeStore2(field, newValue, tableIndex);
            changeStore2(RECOVERABLE, calcRemainingAmount(premiumFloat, newValueFloat), tableIndex);
            // Non-recoverable typed directly — recoverable is now the derived remainder.
            changeStore2(RECOVERABLE_SOURCE, NON_RECOVERABLE, tableIndex);
        }
    }
};

export const onChangeStoreSettings = (event, changeStoreSettings) => {
    const { name, value } = event.target;
    // Default value and sanitize
    const sanitizedInput = (value || '').replace(/[^0-9.]/g, '');
    const sanitizedValue = sanitizedInput ? Math.max(Number(sanitizedInput), 0) : 0;
    changeStoreSettings(name, sanitizedValue);
};

export const checkIfPercentagePremium = (premiumSettingType, wizardIsPercentagePremium) => {
    return (premiumSettingType === PS_TYPE_PERCENTAGE || wizardIsPercentagePremium)
};

// Single definition of "is this the source Default Premium Setting flow" - no solicitor-group scope,
// and not the Application Premium Settings surface. Shared by isDefaultCommissionAddDisabled below and
// premiumSettingsWizardHOC's WI 99823 Non-Contingent lock so the two agree on what "Default" means.
export const isDefaultPremiumSettingFlow = ({ premiumSettingType, isApplicationPremium }) => {
    return premiumSettingType === PS_TYPE_DEFAULT && !isApplicationPremium;
};

// WI: "Enable/disable commission agents on Default Premium Settings" - the Add New Commission
// button must only be disabled for the source Default Premium Setting flow (no solicitor-group
// scope), not for Custom/Percentage settings and not for Application Premium Settings (explicitly
// out of scope for this switch even though they reuse the same wizard component). A Default setting
// copied onto a real Solicitor Group posts a non-null solicitorGroupId, so it does have group scope
// and the button must enable - hence hasSolicitorGroup.
export const isDefaultCommissionAddDisabled = ({ premiumSettingType, isApplicationPremium, featureEnabled, hasSolicitorGroup }) => {
    return isDefaultPremiumSettingFlow({ premiumSettingType, isApplicationPremium }) && !featureEnabled && !hasSolicitorGroup;
};


export const getTableHeaderData = (wizardDamageBands, damages) => {
    let damageBands = [...wizardDamageBands];
    let headerData = [];
    _.forEach(damageBands, band => {
        headerData.push({
            width: '220px',
            label: getDamageBandName(band, damages)
        });
    });
    return headerData;
};